/**
 * A deliberately small markdown model for Biggles replies.
 *
 * The assistant emits a narrow dialect — **bold**, *italic*, `code`, bullet
 * and numbered lists, paragraphs, the odd [link](url) — and a full markdown
 * stack (react-markdown + remark + rehype) is a lot of dependency for that.
 * This parses into a tiny AST the message component renders with theme
 * tokens. Unknown syntax passes through as literal text, never as HTML:
 * there is no HTML path at all, so a reply cannot inject markup.
 */

export type Inline =
  | { type: 'text'; text: string }
  | { type: 'bold'; children: Inline[] }
  | { type: 'italic'; children: Inline[] }
  | { type: 'code'; text: string }
  | { type: 'link'; href: string; children: Inline[] }

export type Block =
  | { type: 'paragraph'; children: Inline[] }
  | { type: 'list'; ordered: boolean; items: Inline[][] }
  // Added 2026-08-23 for ground-school lessons. Additive: Biggles simply
  // never emits these, and one parser beats a second markdown stack for the
  // lesson reader (the presentations differ; the model does not).
  | { type: 'heading'; level: 2 | 3; children: Inline[] }
  | { type: 'quote'; children: Inline[] }
  | { type: 'table'; header: Inline[][]; rows: Inline[][][] }
  /** ![alt](asset:slug) on its own line — resolved by the reader, never a URL. */
  | { type: 'image'; alt: string; asset: string }

const SAFE_HREF = /^(https?:\/\/|mailto:|\/)/i

export function parseInline(src: string): Inline[] {
  const out: Inline[] = []
  let text = ''
  let i = 0
  const flush = () => {
    if (text) out.push({ type: 'text', text })
    text = ''
  }

  while (i < src.length) {
    // `code` — no nesting inside
    if (src[i] === '`') {
      const end = src.indexOf('`', i + 1)
      if (end > i + 1) {
        flush()
        out.push({ type: 'code', text: src.slice(i + 1, end) })
        i = end + 1
        continue
      }
    }
    // **bold**
    if (src.startsWith('**', i)) {
      const end = src.indexOf('**', i + 2)
      if (end > i + 2) {
        flush()
        out.push({ type: 'bold', children: parseInline(src.slice(i + 2, end)) })
        i = end + 2
        continue
      }
    }
    // *italic* — a lone asterisk followed by non-space, closed by non-space + *
    if (src[i] === '*' && src[i + 1] && src[i + 1] !== ' ' && src[i + 1] !== '*') {
      const end = findItalicClose(src, i + 1)
      if (end > i + 1 && src[end - 1] !== ' ') {
        flush()
        out.push({ type: 'italic', children: parseInline(src.slice(i + 1, end)) })
        i = end + 1
        continue
      }
    }
    // [label](href)
    if (src[i] === '[') {
      const close = src.indexOf('](', i + 1)
      const end = close > 0 ? src.indexOf(')', close + 2) : -1
      if (close > i + 1 && end > close + 2) {
        const href = src.slice(close + 2, end).trim()
        if (SAFE_HREF.test(href)) {
          flush()
          out.push({ type: 'link', href, children: parseInline(src.slice(i + 1, close)) })
          i = end + 1
          continue
        }
      }
    }
    text += src[i]
    i++
  }
  flush()
  return out
}

/** Next lone `*` from `from`, stepping over `**` pairs (a nested bold). */
function findItalicClose(src: string, from: number): number {
  let j = from
  while (j < src.length) {
    if (src[j] === '*') {
      if (src[j + 1] === '*') {
        j += 2
        continue
      }
      return j
    }
    j++
  }
  return -1
}

const BULLET =/^\s*[-*•]\s+(.*)$/
const NUMBERED = /^\s*\d+[.)]\s+(.*)$/
const HEADING = /^(#{2,3})\s+(.*)$/
const QUOTE = /^\s*>\s?(.*)$/
const IMAGE = /^\s*!\[([^\]]*)\]\(asset:([a-z0-9][a-z0-9-]*)\)\s*$/i
const TABLE_ROW = /^\s*\|(.+)\|\s*$/
const TABLE_RULE = /^\s*\|[\s:|-]+\|\s*$/

/** Split a `| a | b |` row into its cells. */
function tableCells(line: string): string[] {
  const inner = line.trim().replace(/^\|/, '').replace(/\|$/, '')
  return inner.split('|').map((c) => c.trim())
}

export function parseBlocks(src: string): Block[] {
  const blocks: Block[] = []
  const lines = src.replace(/\r\n?/g, '\n').split('\n')
  let para: string[] = []
  let list: { ordered: boolean; items: string[] } | null = null

  const flushPara = () => {
    if (para.length) blocks.push({ type: 'paragraph', children: parseInline(para.join('\n')) })
    para = []
  }
  const flushList = () => {
    if (list) blocks.push({ type: 'list', ordered: list.ordered, items: list.items.map(parseInline) })
    list = null
  }

  let quote: string[] = []
  const flushQuote = () => {
    if (quote.length) blocks.push({ type: 'quote', children: parseInline(quote.join('\n')) })
    quote = []
  }
  const flushAll = () => {
    flushPara()
    flushList()
    flushQuote()
  }

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i]

    const heading = line.match(HEADING)
    if (heading) {
      flushAll()
      blocks.push({ type: 'heading', level: heading[1].length === 2 ? 2 : 3, children: parseInline(heading[2]) })
      continue
    }

    const image = line.match(IMAGE)
    if (image) {
      flushAll()
      blocks.push({ type: 'image', alt: image[1], asset: image[2].toLowerCase() })
      continue
    }

    const quoted = line.match(QUOTE)
    if (quoted) {
      flushPara()
      flushList()
      quote.push(quoted[1])
      continue
    }
    if (quote.length) flushQuote()

    // A table is a header row followed by a |---| rule; without the rule it is
    // just a line with pipes in it and stays a paragraph.
    if (TABLE_ROW.test(line) && i + 1 < lines.length && TABLE_RULE.test(lines[i + 1])) {
      flushAll()
      const header = tableCells(line).map(parseInline)
      const rows: Inline[][][] = []
      let j = i + 2
      while (j < lines.length && TABLE_ROW.test(lines[j]) && !TABLE_RULE.test(lines[j])) {
        rows.push(tableCells(lines[j]).map(parseInline))
        j++
      }
      blocks.push({ type: 'table', header, rows })
      i = j - 1
      continue
    }

    const bullet = line.match(BULLET)
    const numbered = line.match(NUMBERED)
    if (bullet || numbered) {
      flushPara()
      const ordered = !!numbered
      const item = (bullet ?? numbered)![1]
      if (list && list.ordered !== ordered) flushList()
      if (!list) list = { ordered, items: [] }
      list.items.push(item)
      continue
    }
    if (line.trim() === '') {
      flushAll()
      continue
    }
    // A non-blank, non-bullet line ends a list and starts a paragraph.
    if (list) flushList()
    para.push(line)
  }
  flushAll()
  return blocks
}
